/**
 * Admin → RBAM Matrix
 *
 * Phase 5b item 4 (2026-05-16). Lists every RBAM mapping for the active
 * tenant - i.e. the (role × purposes × allowedSystems × allowedDataClasses
 * × businessUnitScope) tuples that drive permission evaluation. Read-only
 * for now; CustomRoles wizard (item #5) authors the underlying roles.
 *
 * The matrix view groups mappings by role (built-in vs custom) and shows
 * the four scope dimensions side-by-side so a DPO can audit who can see
 * what at a glance.
 *
 * Backend:
 *   GET /v1/foundational/rbam → { rbamMappings: [...] }
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  ShieldCheck,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Layers,
  KeyRound,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import { listRbamMappings, listBusinessUnits, type RbamMapping, type BusinessUnit } from '@/lib/api';

interface RoleGroup {
  roleKey: string;       // 'builtin:dpo' | 'custom:<uuid>'
  roleLabel: string;
  isBuiltIn: boolean;
  mappings: RbamMapping[];
}

const BUILT_IN_LABELS: Record<string, string> = {
  dpo: 'DPO',
  compliance_analyst: 'Compliance Analyst',
  legal_reviewer: 'Legal Reviewer',
  it_admin: 'IT Admin',
  security_operator: 'Security Operator',
  read_only_secops: 'Read-Only Security Operations',
  platform_super_admin: 'Platform Super Admin',
};

function humanizeBuiltIn(s: string): string {
  return BUILT_IN_LABELS[s] ?? s.split('_').map((p) => p[0]?.toUpperCase() + p.slice(1)).join(' ');
}

export function AdminRbamMatrix(): JSX.Element {
  const [rbamMappings, setRbamMappings] = useState<RbamMapping[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const [rbamRes, buRes] = await Promise.allSettled([
        listRbamMappings(),
        listBusinessUnits({}),
      ]);
      const rbam = rbamRes.status === 'fulfilled' ? rbamRes.value.rbamMappings : [];
      const bus = buRes.status === 'fulfilled' ? buRes.value.businessUnits : [];
      setRbamMappings(rbam);
      setBusinessUnits(bus);
      const errs: string[] = [];
      if (rbamRes.status === 'rejected') errs.push(`rbam: ${(rbamRes.reason as Error)?.message ?? 'failed'}`);
      if (buRes.status === 'rejected') errs.push(`business units: ${(buRes.reason as Error)?.message ?? 'failed'}`);
      setError(errs.length > 0 ? errs.join(' · ') : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load RBAM matrix');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Group mappings by role for the matrix view.
  const roleGroups = useMemo<RoleGroup[]>(() => {
    const groups = new Map<string, RoleGroup>();
    for (const m of rbamMappings) {
      const key = m.roleId ? `custom:${m.roleId}` : `builtin:${m.roleBuiltIn ?? 'unknown'}`;
      const label = m.roleBuiltIn ? humanizeBuiltIn(m.roleBuiltIn) : `Custom · ${m.roleId?.slice(0, 8) ?? '?'}…`;
      const isBuiltIn = m.roleBuiltIn != null;
      const existing = groups.get(key);
      if (existing) {
        existing.mappings.push(m);
      } else {
        groups.set(key, { roleKey: key, roleLabel: label, isBuiltIn, mappings: [m] });
      }
    }
    return [...groups.values()].sort((a, b) => {
      if (a.isBuiltIn && !b.isBuiltIn) return -1;
      if (!a.isBuiltIn && b.isBuiltIn) return 1;
      return a.roleLabel.localeCompare(b.roleLabel);
    });
  }, [rbamMappings]);

  const buNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const b of businessUnits) m.set(b.id, b.name);
    return m;
  }, [businessUnits]);

  const stats = useMemo(() => {
    const builtIn = roleGroups.filter((g) => g.isBuiltIn).length;
    const custom = roleGroups.filter((g) => !g.isBuiltIn).length;
    const buScoped = rbamMappings.filter((m) => m.businessUnitScope.length > 0).length;
    return { totalRoles: roleGroups.length, builtIn, custom, buScoped };
  }, [roleGroups, rbamMappings]);

  return (
    <PageContainer>
      <PageHeader
        title="RBAM matrix"
        subtitle="Roles × purposes × allowed systems × allowed data classes × business-unit scope. The matrix the rbam engine evaluates on every request."
        badges={[
          { label: 'Read-only', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Some data unavailable"
          detail={error}
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Distinct roles in matrix" value={stats.totalRoles} icon={Layers} hint="rows below" />
        <KpiCard label="Built-in roles" value={stats.builtIn} icon={ShieldCheck} glow="high" hint="of 7 canonical" />
        <KpiCard label="Custom roles" value={stats.custom} icon={KeyRound} glow={stats.custom > 0 ? 'medium' : 'low'} hint="tenant-authored" />
        <KpiCard label="BU-scoped mappings" value={stats.buScoped} icon={Layers} hint={`of ${rbamMappings.length} total`} />
      </div>

      {loading && <LoadingState caption="Loading RBAM matrix…" />}

      {!loading && rbamMappings.length === 0 && (
        <GlassCard className="p-8 text-center text-sm text-muted-foreground">
          <ShieldCheck className="h-8 w-8 mx-auto mb-3 opacity-40" />
          <div className="font-medium text-foreground/90">No RBAM mappings on file</div>
          <div className="mt-1">
            The platform&rsquo;s 7 canonical roles still apply via their built-in defaults; explicit
            mappings refine those when a tenant needs custom scope.
          </div>
        </GlassCard>
      )}

      {!loading && roleGroups.length > 0 && (
        <div className="space-y-4">
          {roleGroups.map((group, gi) => (
            <motion.div
              key={group.roleKey}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: gi * 0.05 }}
            >
              <GlassCard className="p-5">
                <CardHeader className="p-0 mb-3 flex flex-row items-start justify-between gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      {group.roleLabel}
                      <Badge variant={group.isBuiltIn ? 'success' : 'glass'} className="text-[10px]">
                        {group.isBuiltIn ? 'built-in' : 'custom'}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      {group.mappings.length} mapping{group.mappings.length === 1 ? '' : 's'} attached to this role
                    </CardDescription>
                  </div>
                </CardHeader>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs tabular">
                    <thead>
                      <tr className="text-left text-muted-foreground border-b border-border/30">
                        <th className="py-2 pr-3 font-normal">Purposes</th>
                        <th className="py-2 pr-3 font-normal">Allowed systems</th>
                        <th className="py-2 pr-3 font-normal">Data classes</th>
                        <th className="py-2 pr-3 font-normal">BU scope</th>
                        <th className="py-2 pr-3 font-normal whitespace-nowrap">Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.mappings.map((m) => (
                        <tr key={m.id} className="border-b border-border/15 align-top">
                          <td className="py-2 pr-3">
                            {m.purposes.length === 0 ? (
                              <span className="italic text-muted-foreground">all</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {m.purposes.map((p) => (
                                  <Badge key={p} variant="glass" className="text-[10px] font-mono">{p}</Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            {m.allowedSystems.length === 0 ? (
                              <span className="italic text-muted-foreground">all</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {m.allowedSystems.map((s) => (
                                  <Badge key={s} variant="glass" className="text-[10px] font-mono">{s}</Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            {m.allowedDataClasses.length === 0 ? (
                              <span className="italic text-muted-foreground">all</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {m.allowedDataClasses.map((c) => (
                                  <Badge key={c} variant="glass" className="text-[10px] font-mono">{c}</Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3">
                            {m.businessUnitScope.length === 0 ? (
                              <span className="italic text-muted-foreground">all BUs</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {m.businessUnitScope.map((b) => (
                                  <Badge key={b} variant="glass" className="text-[10px]">
                                    {buNameById.get(b) ?? b.slice(0, 8) + '…'}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="py-2 pr-3 whitespace-nowrap font-mono text-[11px] text-muted-foreground">
                            {new Date(m.updatedAt).toLocaleDateString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      )}
    </PageContainer>
  );
}
